import { IslandType } from '@rta/rta-blueprint-library';
import type { Island, Kitchen } from '@rta/rta-blueprint-library';

import { assignLegacyPageNumbers, assignOverrideScopes } from './assign-page-override-keys';
import { getIslandPageTitle } from './get-island-page-title';
import { getJointCountertopGroups } from './is-joint-countertop';
import { getNonStandardChanges } from './get-non-standard-changes';
import { getOverviewAppliances } from './get-overview-appliances';
import { getSortedIslands } from './get-sorted-islands';
import { needsBacksplashPage } from './needs-backsplash-page';
import { needsFlatTrimPage } from './needs-flat-trim-page';
import { needsSplitOverhead } from './needs-split-overhead';
import type { BlueprintPage, BlueprintPageContent, GetBlueprintPagesParams } from '../types';

// Usable body ≈ 590px (850 − 55/43 padding − ~144px header − ~17px footer) at 19.6px/row ≈ 30 le.
const MAX_LE_PAGE_BODY = 30;

// Every blank user-added sheet carries this same description; the header title is an EditableText, so a user can rename any one of them.
export const CUSTOM_PANEL_PAGE_TITLE = 'CUSTOM PANEL';

// The pre-recalibration budget, kept only to recompute old page numbers so overrides saved against page-number keys still resolve. Do not tune.
const LEGACY_MAX_LE_PAGE_BODY = 45;

const makeContent = (content: Partial<BlueprintPageContent> = {}): BlueprintPageContent => ({
	islands: [],
	panels: [],
	countertops: [],
	showsBaseDepth: false,
	...content
});

type BuildOverviewPagesParams = {
	nonStandardChangeCount: number;
	/** Islands left-to-right by origin.x — must match the order blueprint-kitchen-overview-page renders. */
	orderedIslands: Array<Island>;
	/** Rendered line-equivalents per island, index-aligned with orderedIslands. */
	islandLineCounts: Array<number>;
	maxLePageBody?: number;
};

/** One le per island header + one per rendered appliance row + 0.5 le for the section margin. Counted via the SAME walk the page renders so the two cannot drift apart. */
const getIslandLineCounts = (kitchen: Kitchen, orderedIslands: Array<Island>): Array<number> => {
	return orderedIslands.map((island) => 1 + getOverviewAppliances({ island, kitchen }).length + 0.5);
};

/**
 * Builds the kitchen-overview page(s). Kitchen Specifications (materials + island
 * appliance list) and Customization & Installation Notes (Non-Standard Changes /
 * Kitchen Location / Please Note) always render on separate sheets — no matter
 * whether they'd fit together — because the two sections are read by different
 * audiences (specs by the customer, notes by manufacturing/install) and were
 * cross-contaminating on the same page (GitHub #297).
 *
 * Heights are measured in line-equivalents (le): one 14px text row = 1 le,
 * section margins add fractional le. The budget is derived from the page box
 * (see MAX_LE_PAGE_BODY) rather than tuned against sample designs, and
 * BlueprintKitchenOverviewPage shrinks text to fit as a backstop — so an
 * off-by-a-little estimate costs smaller type on one sheet instead of silently
 * clipping content. `nonStandardChangeCount` is kept in the params for signature
 * stability with the legacy-page-number recompute call site.
 */
const buildOverviewPages = (params: BuildOverviewPagesParams): Array<BlueprintPage> => {
	const { orderedIslands, islandLineCounts, maxLePageBody = MAX_LE_PAGE_BODY } = params;

	// Top block (first page only): materials (3 lines) + margin + layout (1 line) + margin.
	const LE_TOP_CONTENT = 3 + 1 + 1 + 1;

	const overviewPages: Array<BlueprintPage> = [];

	// Degenerate zero-island kitchens: one combined page. `isFirstPage` in the renderer
	// is derived from `startIndex === 0`, so a split here would double-render the
	// materials/layout block on the trailing footer sheet.
	if (orderedIslands.length === 0) {
		overviewPages.push({ pageType: 'kitchen-overview', pageNumber: 0, title: 'KITCHEN SPECIFICATIONS', content: makeContent() });
		return overviewPages;
	}

	// Greedily pack islands into KITCHEN SPECIFICATIONS pages (first page reserves the
	// top materials/layout block; continuation pages use the full body). No footer on
	// any of these — the footer is its own trailing page below.
	let islandIdx = 0;
	let isFirst = true;

	while (islandIdx < orderedIslands.length) {
		const pageStartIdx = islandIdx;
		const maxIslandLines = isFirst ? maxLePageBody - LE_TOP_CONTENT : maxLePageBody;

		let linesUsed = 0;
		let tentativeIdx = islandIdx;
		while (tentativeIdx < orderedIslands.length && linesUsed + islandLineCounts[tentativeIdx] <= maxIslandLines) {
			linesUsed += islandLineCounts[tentativeIdx];
			tentativeIdx++;
		}

		// If no island fits, push one anyway to avoid infinite loop
		if (tentativeIdx === islandIdx) {
			linesUsed += islandLineCounts[tentativeIdx];
			tentativeIdx++;
		}

		islandIdx = tentativeIdx;

		overviewPages.push({
			pageType: 'kitchen-overview',
			pageNumber: 0,
			title: 'KITCHEN SPECIFICATIONS',
			overviewIslandStartIndex: pageStartIdx,
			overviewIslandEndIndex: islandIdx,
			overviewShowFooter: false,
			content: makeContent()
		});

		isFirst = false;
	}

	// Footer block always lives on its own sheet (empty island slice → renders footer only).
	overviewPages.push({
		pageType: 'kitchen-overview',
		pageNumber: 0,
		title: 'CUSTOMIZATION & INSTALLATION NOTES',
		overviewIslandStartIndex: orderedIslands.length,
		overviewIslandEndIndex: orderedIslands.length,
		overviewShowFooter: true,
		content: makeContent()
	});

	return overviewPages;
};

// A narrow bench combines front+back onto one sheet; wider benches get separate sheets. Governed by
// BASE LENGTH (width), not height: Mike (34") & Kevin-E (22") combine; Casey's 36" smoker stand,
// Chris (38"), Hannigan (50"), Clint (62") separate — so the bound is strict.
const COMBINED_FRONT_BACK_MAX_WIDTH = 36;

const combinesFrontBack = (island: Island): boolean => {
	return island.size.width < COMBINED_FRONT_BACK_MAX_WIDTH;
};

export const getBlueprintPages = (params: GetBlueprintPagesParams): Array<BlueprintPage> => {
	const { kitchen, customPages = [] } = params;
	const pages: Array<BlueprintPage> = [];
	let pageNumber = 1;

	// Intro pages (always present)
	pages.push({ pageType: 'cover', pageNumber: pageNumber++, title: '', content: makeContent() });

	// Reserve slot for overview pages; the real split is computed after the
	// per-island pages exist so the non-standard-changes count is accurate.
	const overviewInsertIndex = pages.length;

	// Sort islands strictly left-to-right by origin.x — must match the sort used
	// in blueprint-kitchen-overview-page.tsx so pagination splits align with rendering.
	const orderedIslands = getSortedIslands(kitchen.design.islands.children);

	pages.push({ pageType: 'revision-log', pageNumber: pageNumber++, title: 'REVISION LOG', content: makeContent() });

	// Overhead layout (may split for complex layouts with corner stands)
	const splitOverhead = needsSplitOverhead({ kitchen });
	// No-RTA countertop (material "none"): the overhead sheet is a BASE LAYOUT reference and each
	// per-island countertop sheet is a COUNTERTOP REFERENCE (measurements for on-site templating).
	const isNoRtaCountertop = (kitchen.material?.countertop?.key ?? '') === 'none';
	const countertopPageLabel = isNoRtaCountertop ? 'COUNTERTOP REFERENCE' : 'COUNTERTOP';

	if (splitOverhead) {
		pages.push({ pageType: 'base-layout', pageNumber: pageNumber++, title: 'BASE LAYOUT', content: makeContent() });
		pages.push({ pageType: 'countertop-layout', pageNumber: pageNumber++, title: 'COUNTERTOP LAYOUT', content: makeContent() });
	} else {
		pages.push({ pageType: 'overhead-layout', pageNumber: pageNumber++, title: isNoRtaCountertop ? 'BASE LAYOUT' : 'OVERHEAD LAYOUT', content: makeContent() });
	}

	// Joint countertop groups: clusters of inline, side-connected, collinear, same-depth islands
	// whose tops merge into one slab. Corner (L) islands and any mismatched top are excluded, so
	// they keep their own countertop page. Only members of a group skip the per-island top.
	const jointGroups = getJointCountertopGroups(kitchen);
	const jointIslandIds = new Set<string>(jointGroups.flatMap((group) => group.map((jointIsland) => jointIsland.id)));

	// Per-island pages — use the same left-to-right spatial ordering as the overview.
	for (let i = 0; i < orderedIslands.length; i++) {
		const island = orderedIslands[i];
		// Cache description to avoid type mutation side effects from determineIslandTypeCfn
		const descriptionCache = island.description;
		const islandType = island.type;

		const titleParams = { island, kitchen, islandDescriptionCache: descriptionCache };

		// This island's countertop is replaced by a joint slab when it belongs to a joint group.
		const isJoint = jointIslandIds.has(island.id);

		const frontPanels = island.panels.children.filter((p) => p.isFrontPanel);
		const sidePanels = island.panels.children.filter((p) => p.isSidePanel);
		const backPanels = island.panels.getBackPanels();
		const islandCountertops = kitchen.design.countertops.filterByIslandId(island.id);

		// Standard 4-page island treatment (front, sides, back, countertop) — shared by Grill/Bar,
		// the default case, and wide low benches that don't combine front+back.
		const pushStandardIslandPages = (): void => {
			pages.push({
				pageType: 'front-panel',
				pageNumber: pageNumber++,
				title: getIslandPageTitle({ ...titleParams, pageLabel: 'FRONT PANEL' }),
				islandId: island.id,
				panelId: island.panels.frontPanelId,
				content: makeContent({ islands: [island], panels: frontPanels })
			});

			pages.push({
				pageType: 'side-panels',
				pageNumber: pageNumber++,
				title: getIslandPageTitle({ ...titleParams, pageLabel: 'SIDE PANELS' }),
				islandId: island.id,
				panelId: sidePanels[0]?.id ?? null,
				content: makeContent({ islands: [island], panels: sidePanels, showsBaseDepth: true })
			});

			pages.push({
				pageType: 'back-panel',
				pageNumber: pageNumber++,
				title: getIslandPageTitle({ ...titleParams, pageLabel: 'BACK PANEL' }),
				islandId: island.id,
				panelId: backPanels[0]?.id ?? null,
				content: makeContent({ islands: [island], panels: backPanels.slice(0, 1) })
			});

			if (!isJoint) {
				pages.push({
					pageType: 'countertop',
					pageNumber: pageNumber++,
					title: getIslandPageTitle({ ...titleParams, pageLabel: countertopPageLabel }),
					islandId: island.id,
					panelId: null,
					content: makeContent({ islands: [island], countertops: islandCountertops, showsBaseDepth: true })
				});
			}
		};

		switch (islandType) {
			case IslandType.Asado:
			case IslandType.Smoker: {
				// Narrow low benches combine front+back onto one sheet (3 pages: combined front+back,
				// sides, countertop). Wider benches (Clint 62", David 54") get the standard SEPARATE
				// front/side/back/countertop treatment — handled by the default case below.
				if (!combinesFrontBack(island)) {
					pushStandardIslandPages();
					break;
				}

				pages.push({
					pageType: 'combined-front-back',
					pageNumber: pageNumber++,
					title: getIslandPageTitle({ ...titleParams, pageLabel: 'FRONT AND BACK PANELS' }),
					islandId: island.id,
					panelId: island.panels.frontPanelId,
					content: makeContent({
						islands: [island],
						panels: [...frontPanels, ...backPanels]
					})
				});

				pages.push({
					pageType: 'side-panels',
					pageNumber: pageNumber++,
					title: getIslandPageTitle({ ...titleParams, pageLabel: 'SIDE PANELS' }),
					islandId: island.id,
					panelId: sidePanels[0]?.id ?? null,
					content: makeContent({ islands: [island], panels: sidePanels, showsBaseDepth: true })
				});

				if (!isJoint) {
					pages.push({
						pageType: 'countertop',
						pageNumber: pageNumber++,
						title: getIslandPageTitle({ ...titleParams, pageLabel: countertopPageLabel }),
						islandId: island.id,
						panelId: null,
						content: makeContent({ islands: [island], countertops: islandCountertops, showsBaseDepth: true })
					});
				}
				break;
			}

			case IslandType.Corner: {
				// Corner: front, sides, one combined back-panels sheet (Left+Right), countertop.
				pages.push({
					pageType: 'front-panel',
					pageNumber: pageNumber++,
					title: getIslandPageTitle({ ...titleParams, pageLabel: 'FRONT PANEL' }),
					islandId: island.id,
					panelId: island.panels.frontPanelId,
					content: makeContent({ islands: [island], panels: frontPanels })
				});

				pages.push({
					pageType: 'side-panels',
					pageNumber: pageNumber++,
					title: getIslandPageTitle({ ...titleParams, pageLabel: 'SIDE PANELS' }),
					islandId: island.id,
					panelId: sidePanels[0]?.id ?? null,
					content: makeContent({ islands: [island], panels: sidePanels, showsBaseDepth: true })
				});

				// One "BACK PANELS" sheet — the manufacturing view renders both corner
				// back panels (Left + Right) together, so a per-panel loop duplicates it.
				pages.push({
					pageType: 'back-panel',
					pageNumber: pageNumber++,
					title: getIslandPageTitle({ ...titleParams, pageLabel: 'BACK PANELS' }),
					islandId: island.id,
					panelId: backPanels[0]?.id ?? null,
					content: makeContent({ islands: [island], panels: backPanels })
				});

				if (!isJoint) {
					pages.push({
						pageType: 'countertop',
						pageNumber: pageNumber++,
						title: getIslandPageTitle({ ...titleParams, pageLabel: countertopPageLabel }),
						islandId: island.id,
						panelId: null,
						content: makeContent({ islands: [island], countertops: islandCountertops, showsBaseDepth: true })
					});
				}

				break;
			}

			default: {
				// Standard 4-page: Grill, Bar (front, sides, back, countertop)
				pushStandardIslandPages();
				break;
			}
		}
	}

	// One joint countertop sheet per group — each dimensions only that group's merged tops.
	jointGroups.forEach((group) => {
		pages.push({
			pageType: 'joint-countertop',
			pageNumber: pageNumber++,
			title: 'JOINT GRILL ISLAND COUNTERTOPS',
			// Joint islands get no per-island countertop sheet, so this is where their
			// countertop depth dimension is drawn.
			content: makeContent({
				islands: group,
				countertops: group.flatMap((jointIsland) => kitchen.design.countertops.filterByIslandId(jointIsland.id)),
				showsBaseDepth: true
			})
		});
	});

	// Backsplash page (when any island has backsplashes on its countertop)
	if (needsBacksplashPage({ kitchen })) {
		pages.push({
			pageType: 'backsplash',
			pageNumber: pageNumber++,
			title: 'BACKSPLASH',
			content: makeContent()
		});
	}

	// Flat trim cut list — always the LAST sheet. Kitchen-wide (trims are deduped across
	// every island), and it dimensions the trim pieces themselves, never a panel or countertop.
	if (needsFlatTrimPage({ kitchen })) {
		pages.push({
			pageType: 'flat-trim',
			pageNumber: pageNumber++,
			title: 'FLAT TRIM PIECE',
			content: makeContent()
		});
	}

	// Now that per-island pages exist, count non-standard changes accurately and
	// build the overview split, then splice it into the reserved slot.
	const nonStandardChangeCount = getNonStandardChanges({ kitchen, pages }).length;
	const islandLineCounts = getIslandLineCounts(kitchen, orderedIslands);
	const overviewArgs = { nonStandardChangeCount, orderedIslands, islandLineCounts };
	const overviewPages = buildOverviewPages(overviewArgs);
	pages.splice(overviewInsertIndex, 0, ...overviewPages);

	// Renumber sequentially so downstream page refs and footers stay consistent.
	pages.forEach((page, i) => { page.pageNumber = i + 1; });

	assignOverrideScopes(pages);
	assignLegacyPageNumbers({
		pages,
		overviewInsertIndex,
		overviewCount: overviewPages.length,
		legacyOverviewCount: buildOverviewPages({ ...overviewArgs, maxLePageBody: LEGACY_MAX_LE_PAGE_BODY }).length
	});

	// Blank user-added sheets go last, after every generated page is numbered and scoped: they must
	// not shift an existing sheet's number, they carry no legacy equivalent, and their override scope
	// is keyed to the custom page's own id so deleting one never relocates another's edits.
	customPages.forEach((customPage) => {
		pages.push({
			pageType: 'custom-panel',
			pageNumber: pages.length + 1,
			title: CUSTOM_PANEL_PAGE_TITLE,
			customPageId: customPage.id,
			overrideScope: `custom-panel.${customPage.id}`,
			content: makeContent()
		});
	});

	return pages;
};
